/**
 * PiEditModal — bulk-save Promoting Interoperability attestations.
 *
 * Same replace-all pattern as IA but PI also takes optional numerator and
 * denominator (some PI measures are performance-based, others are
 * yes/no attestations).
 */
import { useEffect, useState } from "react";
import { usePutPiAttestations } from "@/hooks/usePutPiAttestations";
import { useClinicPromotingInterop } from "@/hooks/useClinicPromotingInterop";
import type { PiAttestationItem } from "@/api/models";

export interface PiEditModalProps {
  clinicId: number;
  year: number;
  onClose: () => void;
}

interface DraftRow {
  uiId: string;
  code: string;
  points: string;
  numerator: string;
  denominator: string;
}

function makeDraftRow(): DraftRow {
  return { uiId: crypto.randomUUID(), code: "", points: "10", numerator: "", denominator: "" };
}

function toIntOrNull(s: string): number | null {
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? Math.round(n) : null;
}

export function PiEditModal({ clinicId, year, onClose }: PiEditModalProps) {
  const put = usePutPiAttestations(clinicId);
  // V1.2.0: pre-populate from the new read endpoint. Note: PiAttestationDetail
  // doesn't carry numerator/denominator (the read endpoint only returns
  // measure_id, measure_name, attested_points + audit fields), so num/den
  // come up blank on hydration. The AM re-enters them for performance-based
  // measures — a copy note below explains.
  const readQ = useClinicPromotingInterop(clinicId, { year });
  const [rows, setRows] = useState<DraftRow[]>(() => [makeDraftRow()]);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (hydrated) return;
    if (!readQ.data) return;
    const serverRows = readQ.data.promoting_interop;
    if (serverRows.length === 0) {
      setHydrated(true);
      return;
    }
    setRows(
      serverRows.map((s) => ({
        uiId: crypto.randomUUID(),
        code: s.measure_id,
        points: s.attested_points,
        numerator: "",
        denominator: "",
      })),
    );
    setHydrated(true);
  }, [hydrated, readQ.data]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const updateRow = (uiId: string, patch: Partial<DraftRow>) =>
    setRows((prev) => prev.map((r) => (r.uiId === uiId ? { ...r, ...patch } : r)));
  const removeRow = (uiId: string) => setRows((prev) => prev.filter((r) => r.uiId !== uiId));
  const addRow = () => setRows((prev) => [...prev, makeDraftRow()]);

  const onSave = async () => {
    const attestations: PiAttestationItem[] = rows
      .filter((r) => r.code.trim() !== "")
      .map((r) => {
        const pts = Number(r.points);
        return {
          pi_measure_code: r.code.trim(),
          attested_points: Number.isFinite(pts) ? pts : 0,
          numerator: toIntOrNull(r.numerator),
          denominator: toIntOrNull(r.denominator),
        };
      });
    await put.mutateAsync({
      performance_year: year,
      attestations,
    });
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="pi-edit-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
        <div className="border-b border-border px-5 py-3">
          <h3 id="pi-edit-title" className="text-base font-semibold">
            Promoting Interoperability — {year}
          </h3>
          <p className="mt-1 text-xs text-gray-500">
            {readQ.isLoading
              ? "Loading current attestations…"
              : "Edit or add measures. Save replaces the full PI list. Numerator/denominator pre-populates as blank — re-enter for performance-based measures."}
          </p>
        </div>
        <div className="space-y-2 px-5 py-4">
          {rows.map((row, idx) => (
            <div key={row.uiId} className="flex items-center gap-2">
              <div className="w-8 text-xs font-medium text-gray-400">{idx + 1}.</div>
              <input
                type="text"
                placeholder="Measure code (e.g. PI_EP_1)"
                value={row.code}
                onChange={(e) => updateRow(row.uiId, { code: e.target.value })}
                className="flex-1 rounded border border-border px-2 py-1 text-sm tnum"
              />
              <input
                type="number"
                value={row.points}
                onChange={(e) => updateRow(row.uiId, { points: e.target.value })}
                className="w-16 rounded border border-border px-2 py-1 text-sm tnum"
                aria-label="Points"
                placeholder="Pts"
              />
              <input
                type="number"
                value={row.numerator}
                onChange={(e) => updateRow(row.uiId, { numerator: e.target.value })}
                className="w-20 rounded border border-border px-2 py-1 text-sm tnum"
                aria-label="Numerator"
                placeholder="Num"
              />
              <input
                type="number"
                value={row.denominator}
                onChange={(e) => updateRow(row.uiId, { denominator: e.target.value })}
                className="w-20 rounded border border-border px-2 py-1 text-sm tnum"
                aria-label="Denominator"
                placeholder="Den"
              />
              <button
                type="button"
                onClick={() => removeRow(row.uiId)}
                aria-label="Remove row"
                className="rounded px-1 text-gray-400 hover:bg-gray-100 hover:text-red-600"
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            className="text-sm text-tw-teal-700 hover:underline"
          >
            + Add measure
          </button>
        </div>
        <div className="flex justify-end gap-2 border-t border-border bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={put.isPending}
            className="rounded-button border border-border bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={put.isPending}
            className="rounded-button bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover disabled:opacity-60"
          >
            {put.isPending ? "Saving…" : "Save attestations"}
          </button>
        </div>
      </div>
    </div>
  );
}
